import React from "react";
import { Box, Chip, MenuItem, Select, Stack, Typography } from "@mui/material";

import FieldGroup from "../../../../shared/FieldGroup";
import PromptLevelCheckbox from "../../elements/PromptLevelCheckbox";
import QuickPresetButtons from "../../elements/QuickPresetButtons";
import type { MasteryPromptLevel } from "../../TargetBuilderPanel.types";
import { MASTERY_PROMPT_LEVEL_OPTIONS, PROMPT_LEVELS } from "../../TargetBuilderPanel.types";
import { usePromptHierarchy } from "../../hooks/usePromptHierarchy";

/**
 * Shared editor for the prompt hierarchy used by Discrete Trial, NET, and Task
 * Analysis procedures. Renders nothing when the current procedure has no hierarchy.
 *
 * Hidden by the caller (Section 2 wrapper) when:
 *   - phase === "baseline"
 *   - l2_method === "anchored-rating-scale"
 *   - parent goal's behavior_subject === "caregiver"
 *   - (Task Analysis only) step_scoring !== "prompt-hierarchy"
 */
const PromptHierarchyEditor: React.FC = () => {
  const { hierarchy, hasHierarchy, toggleLevel, applyPreset, setMasteryPromptLevel } =
    usePromptHierarchy();

  if (!hasHierarchy || !hierarchy) return null;

  // Pair levels row-by-row for 2-column grid: [FP,PP], [M,G], [V,I], [Vi,T], [TD]
  const rows: Array<[typeof PROMPT_LEVELS[number], typeof PROMPT_LEVELS[number]?]> = [];
  for (let i = 0; i < PROMPT_LEVELS.length; i += 2) {
    rows.push([PROMPT_LEVELS[i], PROMPT_LEVELS[i + 1]]);
  }

  const selectedLevels = PROMPT_LEVELS.filter((lvl) => hierarchy.levels.includes(lvl.code));

  return (
    <Stack gap={4}>
      <FieldGroup
        label="Prompt Hierarchy"
        description="Select the prompt levels to use during teaching"
      >
        <Stack gap={3}>
          <Box>
            <Typography variant="caption" fontWeight={500} color="text.secondary" display="block" mb={2}>
              Quick Presets:
            </Typography>
            <QuickPresetButtons activePreset={hierarchy.preset} onApply={applyPreset} />
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={500} color="text.secondary" display="block" mb={2}>
              Or select individual levels:
            </Typography>
            <Stack gap={1}>
              {rows.map(([left, right], idx) => (
                <Stack key={idx} direction="row" gap={4}>
                  <Box flex={1} minWidth={180}>
                    <PromptLevelCheckbox
                      code={left.code}
                      label={left.label}
                      checked={hierarchy.levels.includes(left.code)}
                      onToggle={(checked) => toggleLevel(left.code, checked)}
                    />
                  </Box>
                  <Box flex={1} minWidth={180}>
                    {right && (
                      <PromptLevelCheckbox
                        code={right.code}
                        label={right.label}
                        checked={hierarchy.levels.includes(right.code)}
                        onToggle={(checked) => toggleLevel(right.code, checked)}
                      />
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          {selectedLevels.length > 0 && (
            <Stack gap={1} sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
              <Typography variant="caption" fontWeight={500} color="text.secondary">
                Selected Hierarchy ({selectedLevels.length} levels):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedLevels.map((lvl) => (
                  <Chip
                    key={lvl.code}
                    size="small"
                    label={lvl.code}
                    onDelete={() => toggleLevel(lvl.code, false)}
                    sx={{
                      bgcolor: "primary.main",
                      color: "common.white",
                      fontWeight: 600,
                      fontSize: 12,
                      "& .MuiChip-deleteIcon": {
                        color: "common.white",
                        fontSize: 16,
                        "&:hover": { color: "rgba(255,255,255,0.7)" },
                      },
                    }}
                  />
                ))}
              </Box>
            </Stack>
          )}
        </Stack>
      </FieldGroup>

      <FieldGroup
        label="Mastery Prompt Level"
        description="What prompt level counts toward mastery criteria?"
        required
      >
        <Select
          size="small"
          fullWidth
          value={hierarchy.mastery_prompt_level}
          onChange={(e) => setMasteryPromptLevel(e.target.value as MasteryPromptLevel | "")}
          aria-label="Mastery prompt level"
          displayEmpty
          renderValue={(selected) =>
            selected ? (
              MASTERY_PROMPT_LEVEL_OPTIONS.find((o) => o.value === selected)?.label
            ) : (
              <Typography variant="body2" color="text.disabled">
                Select mastery prompt level
              </Typography>
            )
          }
        >
          {MASTERY_PROMPT_LEVEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>
    </Stack>
  );
};

export default PromptHierarchyEditor;
